import { useMemo, useState } from "react";
import "./App.css";
import { useAuth0 } from "@auth0/auth0-react";
import { useEffect } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { useNavigate, useLocation } from "react-router-dom";
import ResponsiveNavbar from "./components/ResponsiveNavbar/ResponsiveAppBar";
import NotFound from "./pages/NotFound";
import "react-toastify/dist/ReactToastify.css";
import "bootstrap/dist/css/bootstrap.css";
import Mint from "./pages/Mint/Mint";
import SetupProfileInitial from "./pages/ProfileSettings/SetupProfileMain";
import SetupProfile from "./pages/ProfileSettings/SetupProfile";
import Explore from "./pages/Explore/Explore";
import MyTracks from "./pages/MyTracks/MyTracks";
import { Messages } from "./components/Messages/Messages";
import { ChatPopup } from "./components/ChatPopup/ChatPopup";
import UserProfile from "./components/UserProfile/UserProfile";
import { AuthContext, UserDetailsContext } from "./contexts";
import { TalkJsContext, S3CredentialsContext } from "./contexts";
import { useTalk } from "./hooks/useTalk";
import NFTPage from "./pages/NFT/NftPage";
import { useGetUser } from "./hooks/useGetUser";
import PageLayout from "./components/PageLayout/PageLayout";
import Daw from "./pages/DAW/Daw";
import Mixpanel from "./utils/mixpanel";
import PrivacyPolicy from "./pages/Legal/Privacy";
import CreatorAgreement from "./pages/Legal/CreatorAgreement";
import TermsOfUse from "./pages/Legal/TermsOfUse";
import WelcomePage from "./pages/Welcome/WelcomePage";
import MetaMaskSetup from "./pages/MetaMaskSetup/MetaMaskSetup";
import ExploreArtists from "./pages/ExploreArtists/ExploreArtists";
import Earnings from "./components/Earnings/Earnings";
import Footer from "./components/Footer/Footer";
import { AdminControls } from "./pages/AdminControls/AdminControls";
import { Billing } from "./pages/Billing/Billing";
import { getStorageUsage } from "./services/storageService";
import Success from "./pages/Billing/Success";
import PaymentCancel from "./pages/Billing/PaymentCancel";
import { SubscriptionState, SubscriptionTier } from "./proto/api_pb";
import { SignUpSplash } from "./pages/SignupSplash/SignupSplash";
import { SingleTrack } from "./pages/TrackPage/TrackPage";
import { EmailVerification } from "./pages/EmailVerification/EmailVerification";
import useGetValidAccessToken from "./hooks/useGetValidAccessToken";
import PricingTable from "./pages/PricingTable/PricingTable";
import { useGetAllUsers } from "./hooks/useGetAllUsers";
import MyFiles from "./pages/MyFiles/MyFiles";
import { TrialEndWarningModal } from "./components/Modal/TrialEndWarningModal";
import ReactPixel from "react-facebook-pixel";
import { SubscriptionUpgradePopUp } from "./components/SubscriptionUpgradePopUp/SubscriptionUpgradePopUp";
import DefaultPage from "./pages/DefaultPage";
import { useS3Credentials } from "./hooks/useS3Credentials";
import { FileUploadStatusCard } from "./components/FileUploadStatusCard/FileUploadStatusCard";
import {
  UploadCardStatusInfo,
  UploadedFileInfo,
} from "./components/FileUploadStatusCard/types";
import { FilePage } from "./pages/FilePage/FilePage";
import { FolderPage } from "./pages/FolderPage/FolderPage";
import { BreadCrumbsObj } from "./pages/FolderPage/types";

const App: React.FC = () => {
  const url: string = window.location.pathname;
  const { loginWithRedirect, getIdTokenClaims, isAuthenticated, isLoading } =
    useAuth0();
  const [userId, setUserId] = useState("");
  const [userVerified, setUserVerified] = useState(false);
  const [storageUsage, setStorageUsage] = useState(0);
  const [successReg, setSuccessReg] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadingFilesDetailsList, setUploadingFilesDetailsList] = useState<
    UploadedFileInfo[]
  >([]);
  const [uploadingCardsStatusList, setUploadingCardsStatusList] = useState<
    UploadCardStatusInfo[]
  >([]);
  const [breadCrumbs, setBreadCrumbs] = useState<BreadCrumbsObj[]>([]);
  const navigate = useNavigate();
  const location = useLocation();
  const { getValidAccessToken } = useGetValidAccessToken();
  const { allUsers, featuredUsers } = useGetAllUsers();
  const isUnwantedPage =
    location.pathname === "/privacy-policy" ||
    location.pathname === "/creator-agreement" ||
    location.pathname === "/terms-of-use" ||
    location.pathname === "/signup";

  const handleSuccess = () => {
    setSuccessReg(true);
  };

  useEffect(() => {
    if (location.pathname === "/login" || location.pathname === "/signup") {
      loginWithRedirect({
        appState: { targetUrl: "/" },
      });
    }
  }, [location.pathname, loginWithRedirect]);

  useEffect(() => {
    Mixpanel.track(window.location.pathname);
    if (process.env.REACT_APP_ENV === "production") {
      ReactPixel.pageView();
    }
  }, [navigate]);

  useEffect(() => {
    (async () => {
      if (isAuthenticated) {
        const idToken = await getIdTokenClaims();
        if (idToken && idToken.sub !== userId) {
          setUserId(idToken.sub);
        }
      }
    })();
  }, [getIdTokenClaims, isAuthenticated, userId]);

  const getStorageUsed = async () => {
    const accessToken = await getValidAccessToken();
    const storageUse = await getStorageUsage(userId, accessToken);
    typeof storageUse === "number"
      ? setStorageUsage(storageUse)
      : setStorageUsage(0);
  };

  const { emailVerified, getUserResponse, getUserError } = useGetUser(userId);
  const { s3Credentials: credentials } = useS3Credentials(userId);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (!isLoading && !isAuthenticated && !isUnwantedPage) {
        navigate("/welcome");
      }
    }, 2000);
    return () => clearTimeout(timer);
  }, [isAuthenticated, isUnwantedPage, isLoading, navigate]);

  useEffect(() => {
    if (!isLoading) {
      if (emailVerified !== undefined && !emailVerified) {
        setUserVerified(false);
        navigate("/email-verification");
      } else if (
        isAuthenticated &&
        getUserError &&
        !isUnwantedPage &&
        !successReg
      ) {
        setUserVerified(false);
        navigate("/initial-setting");
      } else if (getUserResponse) {
        setUserVerified(getUserResponse.getApproved());
        if (getUserResponse.getUuid().length > 0) {
          Mixpanel.identify(getUserResponse.getUuid());
          Mixpanel.people.set({
            first_name: getUserResponse.getFirstName(),
            last_name: getUserResponse.getLastName(),
            username: getUserResponse.getUsername(),
            $email: getUserResponse.getEmail(),
          });
        }
        getStorageUsed();
        if (getUserResponse.getStripeCustomerId() === "") {
          navigate("/pricing-table");
        } else if (
          window.location.pathname === "/" ||
          window.location.pathname === "/initial-setting" ||
          window.location.pathname === "/email-verification" ||
          window.location.pathname === "/signup" ||
          window.location.pathname === "/welcome"
        ) {
          navigate("/my-tracks");
        } else if (
          window.location.pathname === "/pricing-table" &&
          getUserResponse.getStripeCustomerId().length > 0
        ) {
          navigate("/my-tracks");
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getUserError, getUserResponse, isLoading, navigate]);

  const [chatOpen, setChatOpen] = useState(false);

  const handleChatClick = () => {
    setChatOpen(!chatOpen);
    Mixpanel.track(chatOpen ? "messages_popup_close" : "messages_popup_open");
  };

  const {
    autoCompleteOptions,
    isChatLoading,
    session,
    setInbox,
    setParticipants,
    createConversation,
    autoCreateChat,
    setAutoCreateChat,
  } = useTalk(userId);

  const authContextValue = useMemo(() => ({ userId }), [userId]);
  const userDetailsContextValue = useMemo(
    () => ({
      userName: getUserResponse?.getUsername() ?? "",
      fullName:
        getUserResponse?.getFirstName() +
          " " +
          getUserResponse?.getLastName() ?? "",
      connectedWallet: getUserResponse?.getWalletAddressesList()[0] ?? "",
      storageUsage: storageUsage,
      stripeCustomerId: getUserResponse?.getStripeCustomerId() ?? "",
      subscriptionState:
        getUserResponse?.getSubscriptionState() ??
        SubscriptionState.SUBSCRIPTION_STATE_UNSPECIFIED,
      subscriptionTier:
        getUserResponse?.getSubscriptionTier() ??
        SubscriptionTier.SUBSCRIPTION_TIER_UNSPECIFIED,
      allUsers: allUsers,
      featuredUsers: featuredUsers,
    }),
    [getUserResponse, storageUsage, allUsers, featuredUsers]
  );

  const s3CredentialsContextValue = useMemo(
    () => ({
      awsAccessKeyId: credentials?.awsAccessKeyId,
      awsSecretAccessKey: credentials?.awsSecretAccessKey,
      awsSessionToken: credentials?.awsSessionToken,
      expiry: credentials?.expiry,
    }),
    [credentials]
  );

  useEffect(() => {
    if (getUserResponse?.getStripeCustomerId() === "")
      navigate("/pricing-table");
  }, [getUserResponse, navigate]);

  return (
    <>
      {isUploading && (
        <FileUploadStatusCard
          uploadingFilesDetailsList={uploadingFilesDetailsList}
          setIsUploading={setIsUploading}
          uploadingCardsStatusList={uploadingCardsStatusList}
          setUploadingCardsStatusList={setUploadingCardsStatusList}
        />
      )}
      {getUserResponse &&
        !isUnwantedPage &&
        window.location.pathname !== "/pricing-table" && (
          <SubscriptionUpgradePopUp
            subscriptionTier={getUserResponse.getSubscriptionTier()}
          />
        )}
      {getUserResponse && (
        <TrialEndWarningModal
          trialEndTime={getUserResponse.getSubscriptionTrialEnd()}
          userId={userId}
          stripeCustomerId={getUserResponse.getStripeCustomerId()}
        />
      )}
      <ResponsiveNavbar currentUser={getUserResponse} />
      {url !== "/" && url !== "/messages" && isAuthenticated && (
        <ChatPopup chatOpen={chatOpen} handleChatClick={handleChatClick}>
          <Messages
            isSmallScreen={true}
            autoCompleteOptions={autoCompleteOptions}
            isChatLoading={isChatLoading}
            session={session}
            setInbox={setInbox}
            setParticipants={setParticipants}
            createConversation={createConversation}
            autoCreateChat={autoCreateChat}
          />
        </ChatPopup>
      )}
      <AuthContext.Provider value={authContextValue}>
        <UserDetailsContext.Provider value={userDetailsContextValue}>
          <S3CredentialsContext.Provider value={s3CredentialsContextValue}>
            <TalkJsContext.Provider
              value={{
                handleChatClick,
                setParticipants,
                createConversation,
                setAutoCreateChat,
              }}
            >
              <PageLayout>
                <Routes>
                  <Route path="/explore-artists" element={<ExploreArtists />} />
                  <Route path="/not-found" element={<NotFound />} />
                  <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                  <Route
                    path="/creator-agreement"
                    element={<CreatorAgreement />}
                  />
                  <Route path="/terms-of-use" element={<TermsOfUse />} />
                  <Route path="/explore" element={<Explore />} />
                  <Route
                    path="/my-tracks"
                    element={
                      <MyTracks
                        userVerified={userVerified}
                        getStorageUsed={getStorageUsed}
                        setUploadingFilesDetailsList={
                          setUploadingFilesDetailsList
                        }
                        setIsUploading={setIsUploading}
                        setUploadingCardsStatusList={
                          setUploadingCardsStatusList
                        }
                      />
                    }
                  />
                  <Route
                    path="/my-files"
                    element={
                      <MyFiles
                        getStorageUsed={getStorageUsed}
                        setUploadingFilesDetailsList={
                          setUploadingFilesDetailsList
                        }
                        setIsUploading={setIsUploading}
                        setUploadingCardsStatusList={
                          setUploadingCardsStatusList
                        }
                        setBreadCrumbs={setBreadCrumbs}
                      />
                    }
                  />
                  <Route
                    path="/daw"
                    element={<Daw userVerified={userVerified} />}
                  />
                  <Route path={`/nft/:txnID`} element={<NFTPage />} />
                  <Route
                    path="/mint"
                    element={<Mint userVerified={userVerified} />}
                  />
                  <Route
                    path="/initial-setting"
                    element={
                      <SetupProfileInitial handleSuccess={handleSuccess} />
                    }
                  />
                  <Route path="/settings" element={<SetupProfile />} />
                  <Route
                    path="/my-nfts"
                    element={
                      <Earnings
                        username={getUserResponse?.getUsername() ?? ""}
                        connectedWallet={
                          getUserResponse?.getWalletAddressesList()[0] ?? ""
                        }
                      />
                    }
                  />
                  <Route
                    path="/messages"
                    element={
                      <Messages
                        autoCompleteOptions={autoCompleteOptions}
                        isChatLoading={isChatLoading}
                        session={session}
                        setInbox={setInbox}
                        setParticipants={setParticipants}
                        createConversation={createConversation}
                        autoCreateChat={autoCreateChat}
                      />
                    }
                  />
                  <Route
                    path="/messages/:conversationId/:conversationSubject"
                    element={
                      <Messages
                        autoCompleteOptions={autoCompleteOptions}
                        isChatLoading={isChatLoading}
                        session={session}
                        setInbox={setInbox}
                        setParticipants={setParticipants}
                        createConversation={createConversation}
                        autoCreateChat={autoCreateChat}
                      />
                    }
                  />
                  <Route path="admin-controls" element={<AdminControls />} />
                  <Route path="/explore" element={<Explore />} />
                  <Route path="/user-profile/:usr" element={<UserProfile />} />
                  <Route
                    path="*"
                    element={<Navigate to="/not-found" replace={true} />}
                  />
                  <Route path="/pricing-table" element={<PricingTable />} />
                  <Route path="/welcome" element={<WelcomePage />} />
                  <Route path="/logout" element={<WelcomePage />} />
                  <Route path="/" element={<DefaultPage />} />
                  <Route path="/billing" element={<Billing />} />
                  <Route path="/metamask-setup" element={<MetaMaskSetup />} />
                  <Route
                    path="/track/:id"
                    element={
                      <SingleTrack
                        userVerified={userVerified}
                        singleTrackPage={true}
                      />
                    }
                  />
                  <Route path="/file/:id" element={<FilePage />} />
                  <Route
                    path="/folder/:id"
                    element={
                      <FolderPage
                        getStorageUsed={getStorageUsed}
                        setUploadingFilesDetailsList={
                          setUploadingFilesDetailsList
                        }
                        setIsUploading={setIsUploading}
                        setUploadingCardsStatusList={
                          setUploadingCardsStatusList
                        }
                        setBreadCrumbs={setBreadCrumbs}
                        breadCrumbs={breadCrumbs}
                      />
                    }
                  />
                  <Route path="/success" element={<Success />} />
                  <Route path="/payment-cancel" element={<PaymentCancel />} />
                  <Route path="/signup-splash" element={<SignUpSplash />} />
                  <Route path="/signup" element={<WelcomePage />} />
                  <Route path="/login"></Route>
                  <Route
                    path="/email-verification"
                    element={<EmailVerification />}
                  />
                </Routes>
              </PageLayout>
            </TalkJsContext.Provider>
          </S3CredentialsContext.Provider>
        </UserDetailsContext.Provider>
      </AuthContext.Provider>
      <Footer />
    </>
  );
};

export default App;
